import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import HardBreak from '@tiptap/extension-hard-break';
import Placeholder from '@tiptap/extension-placeholder';
import { UndoRedo, Dropcursor, Gapcursor } from '@tiptap/extensions';
import type { Extensions } from '@tiptap/react';
import { RichtextBlock, HeadingBlock, QuoteBlock, CodeBlockNode, DividerBlock, UnknownBlock } from './textBlocks';
import { createAtomBlock } from './atomBlock';
import { TrailingNode, UniqueBlockIds } from './documentPlugins';
import { ImageBlockView } from '../node-views/ImageBlockView';
import { CalloutBlockView } from '../node-views/CalloutBlockView';
import { VideoBlockView, AudioBlockView, FileBlockView, PdfViewerBlockView, EmbedBlockView } from '../node-views/MediaBlockViews';
import { TableBlockView, TimelineBlockView } from '../node-views/TableTimelineViews';
import { InlineQuizBlockView, FillBlankBlockView, MatchingBlockView, OrderingBlockView, FlashcardBlockView, PollBlockView } from '../node-views/InteractiveBlockViews';
import { ColumnsBlockView, AccordionBlockView, TabsBlockView } from '../node-views/LayoutBlockViews';
import { HotspotBlockView, LabelingBlockView, SortingBlockView, SliderScaleBlockView, DiscussionBlockView, BranchingBlockView, SimulationBlockView, ScormEmbedBlockView, H5pBlockView } from '../node-views/ExtendedBlockViews';

// ── Content ─────────────────────────────────────────────────────────
export const ImageBlock = createAtomBlock({ name: 'imageBlock', tag: 'image-block', attributes: { src: '', alt: '', caption: '', alignment: 'center', mediaFileUuid: '' }, view: ImageBlockView });
export const CalloutBlock = createAtomBlock({ name: 'calloutBlock', tag: 'callout-block', attributes: { variant: 'info', icon: '', html: '' }, view: CalloutBlockView });
export const VideoBlock = createAtomBlock({ name: 'videoBlock', tag: 'video-block', attributes: { src: '', poster: '', caption: '', autoplay: false, muted: false, controls: true, chapters: [], mediaFileUuid: '' }, view: VideoBlockView });
export const AudioBlock = createAtomBlock({ name: 'audioBlock', tag: 'audio-block', attributes: { src: '', title: '', autoplay: false, mediaFileUuid: '' }, view: AudioBlockView });
export const FileBlock = createAtomBlock({ name: 'fileBlock', tag: 'file-block', attributes: { src: '', filename: '', filesize: 0, mimeType: '', mediaFileUuid: '' }, view: FileBlockView });
export const EmbedBlock = createAtomBlock({ name: 'embedBlock', tag: 'embed-block', attributes: { url: '', provider: '', caption: '', aspectRatio: '16:9' }, view: EmbedBlockView });
export const PdfViewerBlock = createAtomBlock({ name: 'pdfViewerBlock', tag: 'pdf-viewer-block', attributes: { src: '', filename: '', pageCount: null, mediaFileUuid: '' }, view: PdfViewerBlockView });
export const TimelineBlock = createAtomBlock({ name: 'timelineBlock', tag: 'timeline-block', attributes: { entries: [{ date: '', title: '', description: '' }] }, view: TimelineBlockView });

// ── Layout ──────────────────────────────────────────────────────────
export const TableBlock = createAtomBlock({ name: 'tableBlock', tag: 'table-block', attributes: { rows: [['', '', ''], ['', '', ''], ['', '', '']], hasHeader: true, caption: '' }, view: TableBlockView });
export const ColumnsBlock = createAtomBlock({ name: 'columnsBlock', tag: 'columns-block', attributes: { columns: [] }, view: ColumnsBlockView });
export const AccordionBlock = createAtomBlock({ name: 'accordionBlock', tag: 'accordion-block', attributes: { items: [{ title: '', blocks: [], defaultOpen: true }], allowMultiple: false }, view: AccordionBlockView });
export const TabsBlock = createAtomBlock({ name: 'tabsBlock', tag: 'tabs-block', attributes: { tabs: [{ label: 'Tab 1', blocks: [] }, { label: 'Tab 2', blocks: [] }] }, view: TabsBlockView });

// ── Interactive ─────────────────────────────────────────────────────
export const InlineQuizBlock = createAtomBlock({ name: 'inlineQuizBlock', tag: 'inline-quiz-block', attributes: { question: '', choices: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }], multiSelect: false, explanation: '' }, view: InlineQuizBlockView });
export const FillBlankBlock = createAtomBlock({ name: 'fillBlankBlock', tag: 'fill-blank-block', attributes: { template: '', blanks: [], explanation: '' }, view: FillBlankBlockView });
export const MatchingBlock = createAtomBlock({ name: 'matchingBlock', tag: 'matching-block', attributes: { pairs: [{ left: '', right: '' }, { left: '', right: '' }], explanation: '' }, view: MatchingBlockView });
export const OrderingBlock = createAtomBlock({ name: 'orderingBlock', tag: 'ordering-block', attributes: { items: [{ text: '', correctPosition: 0 }, { text: '', correctPosition: 1 }], explanation: '' }, view: OrderingBlockView });
export const FlashcardBlock = createAtomBlock({ name: 'flashcardBlock', tag: 'flashcard-block', attributes: { cards: [{ front: '', back: '' }] }, view: FlashcardBlockView });
export const PollBlock = createAtomBlock({ name: 'pollBlock', tag: 'poll-block', attributes: { question: '', options: [{ text: '' }, { text: '' }], multiSelect: false, showResults: true }, view: PollBlockView });

// ── Extended interactive (A4, #489) ─────────────────────────────────
export const HotspotBlock = createAtomBlock({ name: 'hotspotBlock', tag: 'hotspot-block', attributes: { imageSrc: '', areas: [], explanation: '' }, view: HotspotBlockView });
export const LabelingBlock = createAtomBlock({ name: 'labelingBlock', tag: 'labeling-block', attributes: { imageSrc: '', labels: [{ x: 50, y: 50, text: '' }], explanation: '' }, view: LabelingBlockView });
export const SortingBlock = createAtomBlock({ name: 'sortingBlock', tag: 'sorting-block', attributes: { categories: [{ label: 'Kategorie 1', items: [''] }, { label: 'Kategorie 2', items: [''] }], explanation: '' }, view: SortingBlockView });
export const SliderScaleBlock = createAtomBlock({ name: 'sliderScaleBlock', tag: 'slider-scale-block', attributes: { question: '', minLabel: '', maxLabel: '', minValue: 1, maxValue: 10, step: 1 }, view: SliderScaleBlockView });
export const DiscussionBlock = createAtomBlock({ name: 'discussionBlock', tag: 'discussion-block', attributes: { prompt: '', allowReplies: true, moderationRequired: false }, view: DiscussionBlockView });
export const BranchingBlock = createAtomBlock({ name: 'branchingBlock', tag: 'branching-block', attributes: { nodes: [{ id: 'n1', question: '', options: [{ label: '' }, { label: '' }] }], startNodeId: 'n1' }, view: BranchingBlockView });
export const SimulationBlock = createAtomBlock({ name: 'simulationBlock', tag: 'simulation-block', attributes: { scenario: '', steps: [{ id: 's1', text: '', choices: [{ label: '', feedback: '', isCorrect: true }, { label: '', feedback: '', isCorrect: false }] }] }, view: SimulationBlockView });
export const ScormEmbedBlock = createAtomBlock({ name: 'scormEmbedBlock', tag: 'scorm-embed-block', attributes: { packageUrl: '', title: '', version: '1.2', mediaFileUuid: '', launchUrl: '' }, view: ScormEmbedBlockView });
export const H5pBlock = createAtomBlock({ name: 'h5pBlock', tag: 'h5p-block', attributes: { embedUrl: '', title: '', contentId: '' }, view: H5pBlockView });

const LAYOUT_BLOCKS = [ColumnsBlock, AccordionBlock, TabsBlock];

interface PlaceholderTexts {
    richtext: string;
    heading: string;
    quote: string;
    code: string;
}

/**
 * All TipTap extensions of the block editor (ported from Scilora `extensions/index.ts`).
 * `nested` editors (inside columns/accordion/tabs) get no layout nodes.
 */
export function getBlockEditorExtensions(placeholders: PlaceholderTexts, options: { nested?: boolean } = {}): Extensions {
    return [
        Document,
        Text,
        UndoRedo,
        Dropcursor,
        Gapcursor,
        HardBreak,
        Bold,
        Italic,
        Strike,
        Underline,
        Link.configure({ openOnClick: false }),
        Placeholder.configure({
            placeholder: ({ node }) => {
                switch (node.type.name) {
                    case 'richtextBlock':
                        return placeholders.richtext;
                    case 'headingBlock':
                        return placeholders.heading;
                    case 'quoteBlock':
                        return placeholders.quote;
                    case 'codeBlockNode':
                        return placeholders.code;
                    default:
                        return '';
                }
            },
        }),
        RichtextBlock,
        HeadingBlock,
        QuoteBlock,
        CodeBlockNode,
        DividerBlock,
        ImageBlock,
        CalloutBlock,
        VideoBlock,
        AudioBlock,
        FileBlock,
        EmbedBlock,
        PdfViewerBlock,
        TimelineBlock,
        TableBlock,
        ...(options.nested ? [] : LAYOUT_BLOCKS),
        InlineQuizBlock,
        FillBlankBlock,
        MatchingBlock,
        OrderingBlock,
        FlashcardBlock,
        PollBlock,
        HotspotBlock,
        LabelingBlock,
        SortingBlock,
        SliderScaleBlock,
        DiscussionBlock,
        BranchingBlock,
        SimulationBlock,
        ScormEmbedBlock,
        H5pBlock,
        UnknownBlock,
        UniqueBlockIds,
        ...(options.nested ? [] : [TrailingNode]),
    ];
}
